import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom'; // Add useSearchParams and useLocation for URL params
import ClientListView from './ClientListView';
import ClientDetailView from './ClientDetailView';
import { useSocket } from '../../context/SocketContext'; // Import useSocket
import TreatmentPathwaysViewer from './TreatmentPathwaysViewer'; // Import the new component
import ConsultationHistory from '../supervisor/ConsultationHistory'; // Import consultation history
import CareNoteAlerts from './CareNoteAlerts'; // Import care note alerts
import TherapistVoiceSettingsV2 from './TherapistVoiceSettingsV2'; // Enhanced multi-provider voice settings
import ClientDefaultsEditor from './ClientDefaultsEditor'; // Therapist-side client defaults (Meeting 3)
import ClientVoiceWhitelist from './ClientVoiceWhitelist'; // Client voice selection whitelist
import AudioExportQueue from './AudioExportQueue'; // Import audio export queue
import TherapistDashboardHome from './dashboard/TherapistDashboardHome'; // Import dashboard
import DashboardV2 from './dashboard/DashboardV2'; // V2 compact urgency-first dashboard
import therapistStyles from './styles/therapistStyles'; // Import new styles
import BoundaryMonitoringDashboard from '../admin/BoundaryMonitoring/BoundaryMonitoringDashboard'; // Import Boundary Monitoring
import TassPanel from './tass/TassPanel'; // TASS assistant sidebar
import TrainingClientsListView from './training/TrainingClientsListView'; // Synthetic-clients-in-therapist-ui (Phase 2)
import { useTherapistUiContextRegistration } from '../../context/TherapistUiContext'; // Help / Report-an-issue context
// MUI Imports
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';

const USE_DASHBOARD_V2 = true;

const TherapistDashboard = ({ token, user }) => {
  const socket = useSocket(); // Get socket from context
  const [searchParams] = useSearchParams(); // Get URL parameters
  const location = useLocation(); // Get location for navigation state
  const [activeView, setActiveView] = useState('dashboard'); // Default to dashboard
  const [allClients, setAllClients] = useState([]); // New state for all clients
  const [selectedClient, setSelectedClient] = useState(null); // Renamed from selectedClientId to store the object
  const [tassOpen, setTassOpen] = useState(false); // TASS sidebar state

  // ST-20260922-0002: ClientDetailView fetches on [selectedClient, ...], so the
  // object handed to it must keep one identity for one selected client. The
  // real-client branch below passes `selectedClient` straight through and is
  // safe; the training-client branch needs `kind: 'synthetic'` forced on, and
  // building that inline meant a new object on every render of this component,
  // which re-ran the fetch effect and re-mounted the tab body each time. One
  // therapist drew 1606 conversation requests in three minutes that way.
  const syntheticSelectedClient = useMemo(
    () => (selectedClient ? { ...selectedClient, kind: 'synthetic' } : null),
    [selectedClient]
  );

  // Publish the dashboard's view + selected client for the TopBar's
  // Help / Report-an-issue dialog. The selected client lives only in this
  // component's state (not the URL), so the chrome cannot derive it itself.
  useTherapistUiContextRegistration('dashboard', {
    activeView,
    selectedClientId: selectedClient?._id || selectedClient?.id || undefined,
    selectedClientKind: selectedClient
      ? (activeView === 'training-clients' || selectedClient.kind === 'synthetic' ? 'synthetic' : 'real')
      : undefined,
  });

  // Ticket 2.5 — TASS summon: ⌘J / Ctrl+J toggles the panel, and the sidebar
  // TASS pin dispatches a loose window event (avoids lifting tassOpen state into
  // a shared context just for one button).
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        setTassOpen((v) => !v);
      }
    };
    const onToggleEvent = () => setTassOpen((v) => !v);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('aimhi:tass:toggle', onToggleEvent);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('aimhi:tass:toggle', onToggleEvent);
    };
  }, []);

  // Handle navigation state (from ConsultationHistory back button)
  useEffect(() => {
    if (location.state?.selectClient) {
      setSelectedClient(location.state.selectClient);
      setActiveView('clients');
      // Clear the navigation state to prevent it from persisting
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Check URL parameters to set active view
  useEffect(() => {
    const tab = searchParams.get('tab');
    const timestamp = searchParams.get('t'); // Check for timestamp to detect navigation clicks

    if (tab === 'clients') {
      setActiveView('clients');
      // Check if a specific clientId was passed in the URL (e.g. from dashboard action cards)
      const clientId = searchParams.get('clientId');
      if (clientId) {
        // Find from loaded clients or create a minimal object for ClientDetailView
        const existing = allClients.find(c => (c._id || c.id) === clientId);
        setSelectedClient(existing || { _id: clientId });
      } else if (!location.state?.selectClient) {
        setSelectedClient(null);
      }
    } else if (tab === 'pathways') {
      setActiveView('pathways');
      setSelectedClient(null);
    } else if (tab === 'consultations') {
      setActiveView('consultations');
      setSelectedClient(null);
    } else if (tab === 'voice-settings') {
      setActiveView('voice-settings');
      setSelectedClient(null);
    } else if (tab === 'client-voice-options') {
      setActiveView('client-voice-options');
      setSelectedClient(null);
    } else if (tab === 'audio-exports') {
      setActiveView('audio-exports');
      setSelectedClient(null);
    } else if (tab === 'boundary-monitoring') {
      setActiveView('boundary-monitoring');
      setSelectedClient(null);
    } else if (tab === 'client-defaults') {
      setActiveView('client-defaults');
      setSelectedClient(null);
    } else if (tab === 'training-clients') {
      // Synthetic-clients-in-therapist-ui (Phase 2): training-clients view
      // shares the dashboard's list/detail pattern. When a clientId is
      // present, jump straight into the synthetic ClientDetailView for that
      // user (kind: 'synthetic') so the materialised practice session opens
      // immediately after start.
      setActiveView('training-clients');
      const clientId = searchParams.get('clientId');
      if (clientId) {
        const existing = allClients.find(c => (c._id || c.id) === clientId);
        // Force kind:'synthetic' on the seed object so ClientDetailView
        // renders banner + chip + tab gating even before hydration completes.
        setSelectedClient(existing ? { ...existing, kind: 'synthetic' } : { _id: clientId, kind: 'synthetic' });
      } else if (!location.state?.selectClient) {
        setSelectedClient(null);
      }
    } else {
      // Default to dashboard if no tab parameter
      setActiveView('dashboard');
      if (!location.state?.selectClient) {
        setSelectedClient(null);
      }
    }
  }, [searchParams, location.state]);

  // No longer needed - navigation happens via header
  // const handleTabChange removed

  const handleClientsFetched = (clients) => { // New handler
    setAllClients(clients);
  };

  const handleClientSelect = (client) => { // Accepts full client object
    setSelectedClient(client);
  };

  const handleBackToList = () => {
    setSelectedClient(null);
  };

  // Helper to get therapist specializations from user object
  const getTherapistSpecializations = () => {
      return user?.therapistInfo?.specializations || [];
  };

  // Admin role check moved to header in App.js

  return (
    <Paper elevation={0} sx={{
      ...therapistStyles.mainCard,
      p: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#fafafa'
    }} data-testid="therapist-dashboard">

      {/* Care Note Alerts - Show on all views */}
      <CareNoteAlerts token={token} />

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Main content area - adjusts width when TASS is open */}
        <Box sx={{
          flex: 1,
          // Reserve space for the open TASS panel. The sidebar pin is the launcher,
          // so no legacy-rail strip is reserved while closed.
          marginRight: tassOpen ? '380px' : '0px',
          transition: 'margin-right 0.3s',
          overflowY: 'auto',
          p: activeView === 'dashboard' ? 0 : 3,
          background: activeView === 'dashboard' ? '#fafafa' : '#ffffff',
          borderRadius: '16px',
          m: 2,
          boxShadow: therapistStyles.mainCard.boxShadow
        }}>
          {activeView === 'dashboard' && (
              USE_DASHBOARD_V2
                ? <DashboardV2 token={token} />
                : <TherapistDashboardHome token={token} />
          )}
          {activeView === 'clients' && (
              // Client View Content: Show list or detail view
              !selectedClient ? (
                  <ClientListView token={token} onClientSelect={handleClientSelect} onClientsFetched={handleClientsFetched} />
              ) : (
                  <ClientDetailView
                      selectedClient={selectedClient} // Pass the full client object
                      onBack={handleBackToList}
                      token={token} // Pass token down if needed
                      user={user} // Pass user down if needed (e.g., for therapist ID)
                      socket={socket} // Pass socket instance
                      initialTab={location.state?.openTab} // Pass the tab to open if coming from consultations
                      initialConversationId={location.state?.conversationId} // Pass the conversation ID to restore
                      initialHighlightedMessageId={location.state?.messageId} // From an inbox notice: open at the exact turn
                  />
              )
          )}
          {activeView === 'pathways' && (
              // Pathways Guide Content
              <TreatmentPathwaysViewer
                  token={token}
                  therapistSpecializations={getTherapistSpecializations()}
              />
          )}
          {activeView === 'consultations' && (
              // Consultations Content
              <ConsultationHistory
                  token={token}
              />
          )}
          {activeView === 'voice-settings' && (
              // Voice Settings Content
              <TherapistVoiceSettingsV2
                  token={token}
              />
          )}
          {activeView === 'client-voice-options' && (
              <ClientVoiceWhitelist token={token} />
          )}
          {activeView === 'audio-exports' && (
              // Audio Exports Queue Content
              <AudioExportQueue />
          )}
          {activeView === 'boundary-monitoring' && (
              // Boundary Monitoring Dashboard
              <BoundaryMonitoringDashboard token={token} userRole={user?.role} />
          )}
          {activeView === 'client-defaults' && (
              // Therapist-side client experience defaults (Meeting 3, T2-2a)
              <ClientDefaultsEditor />
          )}
          {activeView === 'training-clients' && (
              // Synthetic-clients-in-therapist-ui (Phase 2): training clients
              // share the dashboard's list/detail pattern. When a synthetic
              // client is selected, render the full ClientDetailView exactly
              // as for real clients — the banner, chip and tab gating are
              // driven by selectedClient.kind === 'synthetic' inside the view.
              !selectedClient ? (
                  <TrainingClientsListView token={token} onClientSelect={handleClientSelect} />
              ) : (
                  <ClientDetailView
                      selectedClient={syntheticSelectedClient}
                      onBack={handleBackToList}
                      token={token}
                      user={user}
                      socket={socket}
                      initialTab={location.state?.openTab}
                      initialConversationId={location.state?.conversationId}
                      initialHighlightedMessageId={location.state?.messageId}
                  />
              )
          )}
        </Box>

        {/* TASS Sidebar - available across all therapist views */}
        <TassPanel
          token={token}
          open={tassOpen}
          onToggle={() => setTassOpen(!tassOpen)}
          hideCollapsedLauncher={true}
          viewContext={{
            activeView,
            selectedClientId: selectedClient?._id || selectedClient?.id || null,
            selectedClientName: selectedClient?.name || null
          }}
          onNavigateToClient={(clientId, clientName) => {
            const client = allClients.find(c => (c._id || c.id) === clientId);
            setSelectedClient(client || { _id: clientId, name: clientName });
            setActiveView('clients');
          }}
        />
      </Box>
    </Paper>
  );
};

export default TherapistDashboard;
